import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {deleteRecord, fetchRecords} from '../services/SubscriptionItemsService';
import {useState, useEffect} from 'react';
import {SubscriptionItem} from '../types';
import {usePagination} from '../../../hooks/usePagination';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useSubscriptionItems = (customerId: string) => {

    if (typeof customerId === 'undefined') {
        throw new Error('customerId is required parameter')
    }

    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const pagination = usePagination();

    const subscriptionItemsQuery = useQuery({
        queryKey: ['subscriptionItems', customerId, pagination.currentToken],
        queryFn: () => fetchRecords(customerId, pagination.pageSize, pagination.currentToken),
    });

    useEffect(() => {
        if (subscriptionItemsQuery.data) pagination.setNextToken(subscriptionItemsQuery.data.nextToken);
    }, [subscriptionItemsQuery.data]);

    useEffect(() => {
        pagination.resetPagination();
    }, [customerId]);

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(customerId, id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Subscription item deleted');
            queryClient.invalidateQueries({queryKey: ['subscriptionItems', customerId]});
        },
        onError: notifyError,
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({queryKey: ['subscriptionItems', customerId]});
    };

    const deleteRecords = (ids?: string[]) => {
        if (ids && ids.length > 0) {
            deleteMutation.mutate(ids);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems: SubscriptionItem[] = subscriptionItemsQuery.data?.items || [];

    const visibleItems = keyword
        ? allItems.filter(item => {
            const search = keyword.toLowerCase();
            return (
                item.id?.toLowerCase().includes(search) ||
                item.price?.toLowerCase().includes(search) ||
                item.subscription?.toLowerCase().includes(search)
            );
        })
        : allItems;

    return {
        items: allItems,
        visibleItems,
        loading: subscriptionItemsQuery.isLoading,
        keyword,
        visibleCount: visibleItems.length,
        totalCount: allItems.length,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation,
        currentPage: pagination.currentPage,
        hasNext: pagination.hasNext,
        hasPrevious: pagination.hasPrevious,
        goToNextPage: pagination.goToNextPage,
        goToPreviousPage: pagination.goToPreviousPage,
    };
};
